import { Wifi, WifiOff, Bot, Sparkles } from "lucide-react";
import type { MCPServer } from "@/types";
import { cn } from "@/lib/utils";

interface ContactCardProps {
  contact: MCPServer;
  isActive?: boolean;
  onSelect: (id: string) => void;
}

const statusIcon = {
  online: <Wifi className="h-4 w-4 text-emerald-500" />,
  offline: <WifiOff className="h-4 w-4 text-rose-500" />,
  connecting: <Sparkles className="h-4 w-4 text-amber-500" />,
} as const;

const statusLabel = {
  online: "Live",
  offline: "Offline",
  connecting: "Connecting",
} as const;

export const ContactCard = ({ contact, isActive, onSelect }: ContactCardProps) => {
  const gradient = contact.color ?? "from-primary via-sky-500 to-sky-600";

  return (
    <button
      type="button"
      onClick={() => onSelect(contact.id)}
      data-testid={`contact-${contact.id}`}
      className={cn(
        "w-full overflow-hidden rounded-xl sm:rounded-2xl border bg-white/70 p-2.5 sm:p-4 text-left shadow-md transition-all duration-200 hover:-translate-y-1 hover:shadow-xl dark:bg-white/5",
        isActive ? "ring-2 ring-primary shadow-lg" : "border-border/70",
      )}
    >
      <div className="flex items-center gap-2 sm:gap-3 min-w-0 pr-8 sm:pr-10">
        <div className={`h-9 w-9 sm:h-12 sm:w-12 rounded-lg sm:rounded-xl bg-gradient-to-br ${gradient} text-white shadow-md grid place-items-center flex-shrink-0`}>
          <Bot className="h-4 w-4 sm:h-5 sm:w-5" />
        </div>
        <div className="min-w-0 flex-1 overflow-hidden">
          <div className="flex items-center gap-1.5 min-w-0">
            <p className="truncate text-xs sm:text-base font-semibold text-foreground min-w-0">{contact.name}</p>
            <span
              className={cn(
                "inline-flex items-center justify-center rounded-full p-1 sm:px-3 sm:py-1 text-[9px] sm:text-[11px] font-medium flex-shrink-0",
                contact.status === "online" && "bg-emerald-50 text-emerald-700 border border-emerald-100",
                contact.status === "offline" && "bg-rose-50 text-rose-700 border border-rose-100",
                contact.status === "connecting" && "bg-amber-50 text-amber-700 border border-amber-100",
              )}
              title={statusLabel[contact.status]}
            >
              <span className="sm:hidden scale-75">{statusIcon[contact.status]}</span>
              <span className="hidden sm:inline-flex items-center gap-1">
                {statusIcon[contact.status]}
                {statusLabel[contact.status]}
              </span>
            </span>
          </div>
          <p className="truncate text-[10px] sm:text-sm text-muted-foreground mt-0.5">{contact.role?.length ? contact.role?.length > 35 ? contact.role.slice(0, 45) + "..." : contact.role : "No role defined, update profile"}</p>
        </div>
      </div>
      <div className="mt-1.5 sm:mt-3 flex items-center gap-1.5 sm:gap-2 text-[9px] sm:text-xs text-muted-foreground min-w-0">
        <span className="truncate font-mono text-[8px] sm:text-[11px] flex-1 min-w-0">{contact.url}</span>
        <span className="rounded-full bg-secondary/70 px-1.5 py-0.5 sm:px-2.5 sm:py-1 text-[8px] sm:text-[11px] font-semibold text-primary flex-shrink-0 whitespace-nowrap">
          {contact.transport.toUpperCase()}
        </span>
      </div>
    </button>
  );
};
